import { useEffect, useRef, useState } from "react";
import { useDebounce } from "./DebounceHook";

export default function AddressInput({
  address,
  setAddress,
  suggestions,
  setSuggestions,
  setPlaceId,
  // Told when the user says none of Google's suggestions is right. Without this
  // the escape hatch below was a dead end: it closed the list but left the form
  // with no place id, and the form refuses to save a changed address that has no
  // place id. So "keep what I typed" produced a refusal to save - worse than the
  // stuck dropdown it was meant to cure.
  onManualAddress,
  errors,
}) {
  const [isSelected, setIsSelected] = useState(false);
  const debouncedAddress = useDebounce(address, 400);
  const boxRef = useRef(null);

  // Google Places Autocomplete
  useEffect(() => {
    if (
      isSelected || // 🚫 prevent re-fetch after selection
      !window.google ||
      !debouncedAddress ||
      debouncedAddress.length < 3
    ) {
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();

    service.getPlacePredictions(
      {
        input: debouncedAddress,
        // componentRestrictions: { country: "in" },
      },
      (predictions, status) => {
        if (
          status ===
          window.google.maps.places.PlacesServiceStatus.OK
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      }
    );
  }, [debouncedAddress, isSelected, setSuggestions]);

  // The list used to have no way out of it. Picking one of Google's suggestions
  // was the ONLY thing that cleared it - there was no click-outside, no Escape
  // and no blur - so if none of the five offered was the right address the list
  // stayed open on top of the form, covering the opening hours and the close
  // button underneath it. Three ways out now, and none of them changes the
  // address the user has typed.
  useEffect(() => {
    if (!suggestions.length) return undefined;

    const onDocMouseDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        // Swallow it, otherwise CoreUI takes Escape as "close the whole dealer
        // form" and the user loses everything they have typed just because they
        // wanted rid of a dropdown.
        e.stopPropagation();
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [suggestions.length, setSuggestions]);

  return (
    <div style={{ position: "relative", width: "100%" }} ref={boxRef}>
      {/* Address Input */}
      <input
        className="form-control mt-2"
        placeholder="Address"
        value={address}
        onChange={(e) => {
          setAddress(e.target.value);
          setPlaceId(null);
          // Editing again withdraws the "I meant this one" - they get the
          // suggestions back and have to either pick one or say so again.
          if (onManualAddress) onManualAddress(false);
          setIsSelected(false); // 🔓 allow autocomplete again
        }}
        // Tabbing out of the field is also a way of saying "not interested".
        // Delayed, because blur fires before the click on a suggestion lands and
        // clearing immediately would make the list impossible to click.
        onBlur={() => {
          setTimeout(() => {
            if (boxRef.current && boxRef.current.contains(document.activeElement)) return;
            setSuggestions([]);
          }, 150);
        }}
        autoComplete="new-password"
      />

      {errors?.address && (
        <div className="text-danger" style={{ fontSize: "12px" }}>
          {errors.address}
        </div>
      )}

      {/* Suggestions Dropdown */}
      {suggestions.length > 0 && !isSelected && (
        <div
          className="dropdown-menu show"
          style={{
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            zIndex: 9999,
          }}
        >
          {suggestions.map((item) => (
            <div
              key={item.place_id}
              className="dropdown-item"
              style={{ cursor: "pointer" }}
              // THIS IS THE FIX FOR "the suggestions vanish when I click one".
              //
              // Mouse events land in this order: mousedown, then the input
              // loses focus, then mouseup, and only then click. The blur
              // handler above starts a 150 ms timer that empties the list.
              // Hold the button down for longer than that - which is what a
              // normal click is, as opposed to a quick tap - and the row has
              // already been removed by the time the click would have fired.
              // The click lands on nothing and the address is never taken.
              //
              // That is why it looked intermittent: a fast click worked, an
              // ordinary one did not.
              //
              // preventDefault on mousedown stops the input losing focus at
              // all, so there is no blur, no timer, and the click always
              // arrives. The proof that this is right was already in this file:
              // the "None of these" row below has carried this line from the
              // start, and that row is the one that always worked.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setAddress(item.description);
                setPlaceId(item.place_id);
                if (onManualAddress) onManualAddress(false);
                setSuggestions([]);
                setIsSelected(true); // 🔒 stop autocomplete
              }}
            >
              {item.description}
            </div>
          ))}

          {/* Google will not always have the address, and a townland often is not
              on it at all. Without this the only way out was to accept a place
              that is not where the business is. */}
          <div
            className="dropdown-item text-muted"
            style={{ cursor: "pointer", borderTop: "1px solid #e6e6e6" }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setSuggestions([]);
              setIsSelected(true);
              // Say so out loud, so the form knows this address was typed on
              // purpose rather than left half-finished, and lets it save.
              if (onManualAddress) onManualAddress(true);
            }}
          >
            None of these — keep what I typed
          </div>
        </div>
      )}
    </div>
  );
}
